import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
  IconographyThemes,
  Button,
  Toggle,
  ToggleSizes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { getCategoriesSagaAction } from "../../../../../../redux/saga/project-validation/saga-actions";
import "../Modals.scss";

interface AddTestCaseModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  editData?: any;
}

const AddTestCaseModal: React.FC<AddTestCaseModalProps> = ({
  open,
  onClose,
  onSave,
  editData,
}) => {
  const dispatch = useAppDispatch();
  const { categories, status } = useAppSelector(
    (state) => state.project_validation
  );
  const [submitted, setSubmitted] = useState(false);

  const isSubmitting = editData
    ? status.updateTestCaseLoading
    : status.createTestCaseLoading;
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    category: "",
    highLevelScenario: "",
    preConditions: "",
    description: "",
    expectedResult: "",
    status: true,
  });
  useEffect(() => {
    if (open && categories.length === 0) {
      dispatch(getCategoriesSagaAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Update form when editData changes
  React.useEffect(() => {
    if (editData) {
      setFormData({
        category: String(editData.category_id || ""),
        highLevelScenario: editData.high_level_scenario || "",
        preConditions: editData.pre_conditions || "",
        description: editData.description || "",
        expectedResult: editData.expected_result || "",
        status: editData.test_case_status === "Active" || editData.test_case_status === "active",
      });
    } else {
      // Reset form for new entry
      setFormData({
        category: "",
        highLevelScenario: "",
        preConditions: "",
        description: "",
        expectedResult: "",
        status: true,
      });
    }
    setSubmitted(false);
    setErrors({});
  }, [editData, open]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const name = e.target.name as string;
    setFormData({
      ...formData,
      [name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      status: e.target.checked,
    });
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm() && !isSubmitting) {
      setSubmitted(true);
      onSave(formData);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSubmitted(false);
      setErrors({});
      setFormData({
        category: "",
        highLevelScenario: "",
        preConditions: "",
        description: "",
        expectedResult: "",
        status: true,
      });
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className="test-case-modal"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {submitted ? (
        <div
          style={{
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {isSubmitting ? (
            <>
              <Loader variant={LoaderSizes.large} />
              <Typography
                variant="body2"
                sx={{ color: "#374151", textAlign: "center" }}
              >
                {editData ? "Updating test case..." : "Creating test case..."}
              </Typography>
            </>
          ) : (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <Typography
                variant="body2"
                sx={{ color: "#374151", textAlign: "center" }}
              >
                {editData
                  ? "Test case updated successfully"
                  : "Test case added successfully"}
              </Typography>
              <Button onClick={handleClose} variant="primary" label="Close" />
            </>
          )}
        </div>
      ) : (
        <>
          <DialogTitle className="modal-title">
            {editData ? "Update Test Case" : "Add Test Case"}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              disabled={isSubmitting}
              className="close-button"
            >
              <Iconography icon="xmark" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers className="modal-content">
            <FormControl
              fullWidth
              margin="none"
              required
              error={!!errors.category}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F7F7F7",
                },
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#d32f2f !important",
                  },
              }}
            >
              <label>
                Category <span className="required">*</span>
              </label>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange as any}
                displayEmpty
                renderValue={(value) => {
                  if (!value) {
                    return <span style={{ color: "#9ca3af" }}>Select</span>;
                  }
                  return categories.find((category) => String(category.ID) === value)?.category_name || "";
                }}
              >
                <MenuItem value="">Select</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.ID} value={String(category.ID)}>
                    {category.category_name}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && (
                <Typography
                  sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0.5 }}
                >
                  {errors.category}
                </Typography>
              )}
            </FormControl>

            <div className="text-field-wrapper">
              <div className="field-label-row">
                <label className="field-label">High Level Scenario</label>
                <div className="char-counter">
                  {formData.highLevelScenario.length}/1000
                </div>
              </div>
              <TextField
                fullWidth
                name="highLevelScenario"
                value={formData.highLevelScenario}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Enter high level scenario"
                variant="outlined"
              />
            </div>

            <div className="text-field-wrapper">
              <div className="field-label-row">
                <label className="field-label">Pre-Conditions</label>
                <div className="char-counter">
                  {formData.preConditions.length}/1000
                </div>
              </div>
              <TextField
                fullWidth
                name="preConditions"
                value={formData.preConditions}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Enter pre-conditions"
                variant="outlined"
              />
            </div>

            <div className="text-field-wrapper">
              <div className="field-label-row">
                <label className="field-label">Description</label>
                <div className="char-counter">
                  {formData.description.length}/1000
                </div>
              </div>
              <TextField
                fullWidth
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Enter description"
                variant="outlined"
              />
            </div>

            <div className="text-field-wrapper">
              <div className="field-label-row">
                <label className="field-label">Expected result</label>
                <div className="char-counter">
                  {formData.expectedResult.length}/1000
                </div>
              </div>
              <TextField
                fullWidth
                name="expectedResult"
                value={formData.expectedResult}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Enter expected result"
                variant="outlined"
              />
            </div>

            <div className="status-field">
              <span className="status-label">Test Case Status</span>
              <div className="status-toggle-wrapper">
                <Toggle
                  id="testcase-status"
                  labelText=""
                  labelON=""
                  labelOFF=""
                  onClick={(e) =>
                    handleSwitchChange({
                      target: { checked: !formData.status },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  toggled={formData.status}
                  size={ToggleSizes.Mini}
                />
                <span className="status-text">
                  {formData.status ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            <div className="button-actions">
              <Button
                onClick={handleClose}
                variant="secondary"
                label="Cancel"
                disabled={isSubmitting}
              />
              <Button
                onClick={handleSubmit}
                variant="primary"
                label={editData ? "UPDATE" : "SAVE"}
                disabled={isSubmitting}
              />
            </div>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default AddTestCaseModal;
